import axios, { AxiosHeaders } from "axios";
import PaginatedResponse from "common/contracts/paginated-response";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import PermissionTemplateCommand from "permissions/entities/permission/api/request-contracts/permission-template-command";
import PermissionTemplateQuery from "permissions/entities/permission/api/request-contracts/permission-template-query";
import PermissionTemplateAPIResponse from "permissions/entities/permission/api/response-contracts/permission-template-api-response";
import PermissionTemplate from "permissions/entities/permission/permission-template";
import Session from "users/session/session";

export default class PermissionTemplatesAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  private headersWithJson(): AxiosHeaders {
    return this.headers.concat({ "Content-Type": "application/json" });
  }

  constructor(session: Readonly<Session>) {
    if (!session.authToken) {
      throw new Error("Cannot create PermissionsTemplateAPIService without session.");
    }
    this.headers.set(
      "Authorization",
      `Bearer ${session.authToken.value}`
    );
  }

  async addPermissionTemplate(
    template: PermissionTemplate,
    abortController?: AbortController
  ): Promise<PermissionTemplate> {
    const response = await axios.post(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/templates`,
      new PermissionTemplateCommand(template),
      {
        headers: this.headersWithJson(),
        signal: abortController?.signal
      }
    );

    const apiResponse = Object.assign(
      new PermissionTemplateAPIResponse(),
      response.data
    );
    return await apiResponse.deserialize();
  }

  async getPermissionTemplates(
    query: PermissionTemplateQuery,
    abortController?: AbortController
  ): Promise<PaginatedResponse<PermissionTemplate>> {
    const response = await axios.get(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/templates?${query.asSearchParams().toString()}`,
      {
        headers: this.headers,
        signal: abortController?.signal
      }
    );

    const responseData: PermissionTemplate[] = [];
    for (const datum of response.data) {
      const apiResponse: PermissionTemplateAPIResponse = Object.assign(
        new PermissionTemplateAPIResponse(),
        datum
      );
      const responseDatum = await apiResponse.deserialize();
      responseData.push(responseDatum);
    }

    return PaginatedResponse.from<PermissionTemplate>(
      responseData, response.headers['x-pagination']
    )
  }

  async getPermissionTemplateById(
    id: string,
    abortController?: AbortController
  ): Promise<PermissionTemplate> {
    const response = await axios.get(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/templates/${id}`,
      {
        headers: this.headers,
        signal: abortController?.signal
      }
    );
    const apiResponse = Object.assign(
      new PermissionTemplateAPIResponse(),
      response.data
    );
    return await apiResponse.deserialize();
  }

  async updatePermissionTemplate(
    template: PermissionTemplate,
    abortController?: AbortController
  ): Promise<PermissionTemplate> {
    const response = await axios.put(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/templates/${template.id}`,
      new PermissionTemplateCommand(template),
      {
        headers: this.headersWithJson(),
        signal: abortController?.signal
      }
    );

    const apiResponse = Object.assign(
      new PermissionTemplateAPIResponse(),
      response.data
    );
    return await apiResponse.deserialize();
  }

  async deletePermissionTemplateById(
    id: string,
    abortController?: AbortController
  ): Promise<void> {
    await axios.delete(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/templates/${id}`,
      {
        headers: this.headers,
        signal: abortController?.signal
      }
    );
  }
}

export class PermissionsTemplateAPIServiceError extends Error {
  public methodName: string;
  public error: unknown;

  constructor(methodName: string, error: unknown) {
    super(`Error in PermissionsTemplateAPIService.${methodName}: ${error}`);
    this.methodName = methodName;
    this.error = error;
  }
}
